import { ClampedText } from '../../../components/ClampedText'
import { Check, Layers, Link2, Loader2, Pause, Play, Search, Unlink, X } from 'lucide-react'
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react'
import type React from 'react'
import {
  createSunoPromptPool,
  getSimilarSunoPrompts,
  getSunoPromptPools,
  getSunoPromptTracks,
  linkSunoPromptTracks,
  mergeSunoPromptPool,
  unlinkAudioTrackSunoPrompt,
  updateSunoPromptPool,
  type SunoPrompt,
  type SunoPromptSimilarityCandidate,
  type SunoPromptSimilarityResult,
  type SunoPromptTrackBindingSummary,
  type SunoPromptTrackListResult,
  type SunoPromptTrackSummary
} from '../../../api/suno'
import { useToast } from '../../../components/ToastProvider'
import { Button, DataTableElement, DataTableSurface, Drawer, IconButton, Modal, ModalActions, Pill, TablePagination, adminMediaPlayerClassName } from '../../../components/ui'
import { AdminAudioPlayer } from '../../audio/AdminAudioPlayer'
import { useAudioPlayer, type PlayableTrack } from '../../audio/useAudioPlayer'
import styles from '../../../pages/ToolsPage.module.css'

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const pc = adminMediaPlayerClassName

type SunoPromptTracksDrawerProps = {
  onChanged: () => void
  onClose: () => void
  prompt: SunoPrompt
}

type SunoPromptSimilarityReviewProps = {
  audioCurrentTime: number
  audioDuration: number
  audioLoading: boolean
  audioMuted: boolean
  audioPlaying: boolean
  audioVolume: number
  loadingPromptId: string | null
  onChanged: () => void
  onClose: () => void
  onNextTrack?: () => void
  onOpenPool: (poolId: string) => void
  onPlayPromptTracks: (prompt: SunoPrompt) => Promise<void>
  onSeek: (time: number) => void
  onStopPlayback: () => void
  onToggleMute: () => void
  onTogglePlayback: () => void
  onVolumeChange: (volume: number) => void
  playbackPromptId: string | null
  playbackTracks: PlayableTrack[]
  playingTrack: PlayableTrack | null
  prompt: SunoPrompt
}

const similarityReasonLabels: Record<SunoPromptSimilarityCandidate['reason'], string> = {
  exact_match: 'Exact match',
  same_group_unpooled: 'Same group',
  same_style_different_exclude: 'Same style, different exclude',
  similar_style: 'Similar style'
}

export const SunoPromptSimilarityReview = ({
  audioCurrentTime,
  audioDuration,
  audioLoading,
  audioMuted,
  audioPlaying,
  audioVolume,
  loadingPromptId,
  onChanged,
  onClose,
  onNextTrack,
  onOpenPool,
  onPlayPromptTracks,
  onSeek,
  onStopPlayback,
  onToggleMute,
  onTogglePlayback,
  onVolumeChange,
  playbackPromptId,
  playbackTracks,
  playingTrack,
  prompt
}: SunoPromptSimilarityReviewProps) => {
  const { toast } = useToast()
  const [data, setData] = useState<SunoPromptSimilarityResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedPromptIds, setSelectedPromptIds] = useState<string[]>([prompt.id])
  const [canonicalPromptId, setCanonicalPromptId] = useState(prompt.id)

  const selectedCandidates = data?.items.filter((candidate) => selectedPromptIds.includes(candidate.prompt.id)) ?? []
  const selectedStandaloneIds = selectedCandidates
    .filter((candidate) => !candidate.merge_pool)
    .map((candidate) => candidate.prompt.id)
  const selectedMergePoolIds = Array.from(new Set(
    selectedCandidates
      .map((candidate) => candidate.merge_pool?.id)
      .filter((poolId): poolId is string => Boolean(poolId))
  ))
  const closeCandidates = data?.items.filter((candidate) => candidate.reason !== 'same_group_unpooled') ?? []
  const sameGroupCandidates = data?.items.filter((candidate) => candidate.reason === 'same_group_unpooled') ?? []
  const canSave = prompt.pool
    ? selectedStandaloneIds.length > 0 || selectedMergePoolIds.length > 0
    : selectedPromptIds.length >= 2 && selectedMergePoolIds.length === 0

  const renderPromptTrackPlayButton = (targetPrompt: SunoPrompt): ReactNode => {
    if ((targetPrompt.linked_track_count ?? 0) <= 0) {
      return null
    }

    const isPromptLoading = loadingPromptId === targetPrompt.id || (audioLoading && playbackPromptId === targetPrompt.id)
    const isPromptPlaying = Boolean(
      playingTrack
      && playbackPromptId === targetPrompt.id
      && playbackTracks.some((track) => track.id === playingTrack.id)
      && audioPlaying
    )

    return (
      <IconButton
        disabled={isPromptLoading}
        icon={isPromptLoading ? (
          <span className="track-status-spinner" />
        ) : isPromptPlaying ? (
          <Pause size={15} />
        ) : (
          <Play size={15} />
        )}
        iconSize="play"
        label={isPromptPlaying ? `Pause tracks for ${targetPrompt.title}` : `Play tracks for ${targetPrompt.title}`}
        loading={isPromptLoading}
        onClick={() => void onPlayPromptTracks(targetPrompt)}
        title={isPromptLoading ? 'Loading' : isPromptPlaying ? 'Pause' : 'Play linked tracks'}
      />
    )
  }

  useEffect(() => {
    let ignore = false

    const load = async (): Promise<void> => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await getSimilarSunoPrompts(prompt.id)

        if (!ignore) {
          setData(result)
        }
      } catch (requestError) {
        if (!ignore) {
          setError(requestError instanceof Error ? requestError.message : 'Unable to load similar prompts.')
        }
      } finally {
        if (!ignore) {
          setIsLoading(false)
        }
      }
    }

    void load()

    return () => {
      ignore = true
    }
  }, [prompt.id])

  const toggleCandidate = (candidate: SunoPromptSimilarityCandidate): void => {
    setSelectedPromptIds((current) =>
      current.includes(candidate.prompt.id)
        ? current.filter((id) => id !== candidate.prompt.id)
        : [...current, candidate.prompt.id]
    )
  }

  const save = async (): Promise<void> => {
    if (!canSave) {
      return
    }

    setIsSaving(true)
    setError(null)

    try {
      if (prompt.pool) {
        let currentPool = (await getSunoPromptPools()).find((pool) => pool.id === prompt.pool?.id)

        if (!currentPool) {
          throw new Error('Prompt pool not found.')
        }

        for (const sourcePoolId of selectedMergePoolIds) {
          currentPool = await mergeSunoPromptPool({
            canonicalPromptId,
            sourcePoolId,
            targetPoolId: currentPool.id
          })
        }

        const promptIds = Array.from(new Set([
          ...currentPool.variants.map((variant) => variant.prompt.id),
          ...selectedStandaloneIds
        ]))

        await updateSunoPromptPool({
          canonicalPromptId,
          description: currentPool.description,
          id: currentPool.id,
          name: currentPool.name,
          promptIds
        })
        toast('Prompt pool updated.')
      } else {
        await createSunoPromptPool({
          canonicalPromptId,
          name: prompt.title,
          promptIds: selectedPromptIds
        })
        toast('Prompt pool created.')
      }

      onChanged()
      onClose()
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save prompt pool.')
    } finally {
      setIsSaving(false)
    }
  }

  const renderCandidateRow = (candidate: SunoPromptSimilarityCandidate) => {
    const selected = selectedPromptIds.includes(candidate.prompt.id)
    const disabled = !prompt.pool && Boolean(candidate.merge_pool)

    return (
      <tr className={disabled ? cx(styles['prompt-similarity-row'], 'disabled') : cx(styles['prompt-similarity-row'])} key={candidate.prompt.id}>
        <td className={styles['prompt-similarity-title-cell']} data-label="Prompt">
          <label className={styles['prompt-similarity-select']}>
            <input
              checked={selected}
              disabled={disabled || isSaving}
              onChange={() => toggleCandidate(candidate)}
              type="checkbox"
            />
            <span className={styles['prompt-similarity-title-copy']}>
              <strong>{candidate.prompt.title}</strong>
              <Pill>{Math.round(candidate.score * 100)}%</Pill>
            </span>
          </label>
          {canonicalPromptId === candidate.prompt.id && (
            <div className={styles['prompt-similarity-title-meta']}>
              <Pill>Canonical</Pill>
            </div>
          )}
          {candidate.prompt.groups.length > 0 && (
            <span className={styles['prompt-similarity-groups']}>
              {candidate.prompt.groups.map((group) => group.name).join(', ')}
            </span>
          )}
        </td>
        <td className={styles['prompt-text-cell']} data-label="Style">
          <div className={cx(styles['prompt-text-cell-inner'], styles['prompt-similarity-prompt-preview'])}>
            <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{candidate.prompt.style_prompt}</code></ClampedText>
          </div>
        </td>
        <td className={cx(styles['prompt-exclude-cell'], styles['prompt-text-cell'])} data-label="Exclude">
          {candidate.prompt.exclude_prompt ? (
            <div className={cx(styles['prompt-text-cell-inner'], styles['prompt-similarity-prompt-preview'])}>
              <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{candidate.prompt.exclude_prompt}</code></ClampedText>
            </div>
          ) : (
            <span className="muted">-</span>
          )}
        </td>
        <td className={styles['prompt-tracks-cell']} data-label="Tracks">
          <div className={styles['prompt-similarity-track-actions']}>
            {renderPromptTrackPlayButton(candidate.prompt)}
            <span className={styles['prompt-similarity-track-count']}>
              <Link2 size={15} />
              {candidate.prompt.linked_track_count ?? 0}
            </span>
          </div>
        </td>
        <td className={styles['prompt-similarity-actions-cell']} data-label="Actions">
          <div className={styles['prompt-similarity-row-actions']}>
            <IconButton
              className={canonicalPromptId === candidate.prompt.id ? 'active' : ''}
              disabled={disabled || isSaving}
              icon={<Check size={15} />}
              label={`Set ${candidate.prompt.title} as canonical`}
              onClick={() => setCanonicalPromptId(candidate.prompt.id)}
              title="Set canonical"
            />
            {candidate.merge_pool ? (
              <IconButton
                icon={<Layers size={15} />}
                label={`Open pool ${candidate.merge_pool.name}`}
                onClick={() => onOpenPool(candidate.merge_pool!.id)}
                title={`Open pool ${candidate.merge_pool.name}`}
              />
            ) : null}
          </div>
        </td>
      </tr>
    )
  }

  return (
    <Modal
      ariaLabelledBy="prompt-similarity-title"
      backdropClassName={cx(styles['prompt-library-backdrop'])}
      isDismissDisabled={isSaving}
      onClose={onClose}
      panelClassName={cx(styles['prompt-library-panel'], styles['prompt-similarity-panel'])}
    >
      <div className="ui-modal-heading modal-heading">
        <div>
          <h2 id="prompt-similarity-title">Similar prompts</h2>
          <p>{prompt.title}</p>
        </div>
        <IconButton disabled={isSaving} icon={<X size={17} />} label="Close similar prompts" onClick={onClose} />
      </div>

      {error && <div className={styles['tools-error']}>{error}</div>}

      <div className={styles['prompt-similarity-content']}>
        <aside className={styles['prompt-similarity-root']} aria-label="Canonical prompt">
          <div className={styles['prompt-similarity-root-header']}>
            <div className={styles['prompt-similarity-root-title']}>
              <strong>{prompt.title}</strong>
              {renderPromptTrackPlayButton(prompt)}
            </div>
            {prompt.pool && <Pill>Pool: {prompt.pool.name}</Pill>}
          </div>
          <label className={styles['prompt-canonical-option']}>
            <input
              checked={canonicalPromptId === prompt.id}
              name="canonical-prompt"
              onChange={() => setCanonicalPromptId(prompt.id)}
              type="radio"
            />
            Canonical
          </label>
          <div className={cx(styles['prompt-text-cell-inner'], styles['prompt-similarity-prompt-preview'])}>
            <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{prompt.style_prompt}</code></ClampedText>
          </div>
          {prompt.exclude_prompt && (
            <div className={cx(styles['prompt-text-cell-inner'], styles['prompt-similarity-prompt-preview'])}>
              <ClampedText className={cx(styles['prompt-code-clamp'])}><code>{prompt.exclude_prompt}</code></ClampedText>
            </div>
          )}
          <div className={styles['prompt-similarity-root-meta']}>
            <span>{selectedPromptIds.length} selected</span>
            <span>{data ? `${data.items.length} candidates` : 'Candidates loading'}</span>
          </div>
        </aside>

        <DataTableSurface className={styles['prompt-similarity-table-card']} aria-label="Similar prompt candidates" mobileStack>
          {isLoading && (
            <div className={cx(styles['tools-results-state'], styles['prompt-results-state'])}>
              <Loader2 className="tools-spin" size={24} />
              <span>Finding similar prompts...</span>
            </div>
          )}

          {!isLoading && data && data.items.length === 0 && (
            <div className={cx(styles['tools-results-state'], styles['prompt-results-state'])}>
              <Search size={24} />
              <span>No close prompts found.</span>
            </div>
          )}

          {!isLoading && data && data.items.length > 0 && (
            <div className={styles['prompt-similarity-table-scroll']}>
              <DataTableElement className={cx(styles['prompt-table'], styles['prompt-similarity-table'])}>
                <colgroup>
                  <col className={styles['prompt-similarity-col-title']} />
                  <col className={styles['prompt-similarity-col-style']} />
                  <col className={styles['prompt-similarity-col-exclude']} />
                  <col className={styles['prompt-similarity-col-tracks']} />
                  <col className={styles['prompt-similarity-col-actions']} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Prompt</th>
                    <th>Style prompt</th>
                    <th>Exclude</th>
                    <th>Tracks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {closeCandidates.length > 0 && (
                    <>
                      <tr className={styles['prompt-similarity-section-row']}>
                        <td colSpan={5}>
                          <div className={styles['prompt-similarity-section-row-content']}>
                            <span>Close candidates</span>
                            <Pill>{closeCandidates.length}</Pill>
                          </div>
                        </td>
                      </tr>
                      {closeCandidates.map(renderCandidateRow)}
                    </>
                  )}
                  {sameGroupCandidates.length > 0 && (
                    <>
                      <tr className={styles['prompt-similarity-section-row']}>
                        <td colSpan={5}>
                          <div className={styles['prompt-similarity-section-row-content']}>
                            <span>Same group, unpooled</span>
                            <Pill>{sameGroupCandidates.length}</Pill>
                          </div>
                        </td>
                      </tr>
                      {sameGroupCandidates.map(renderCandidateRow)}
                    </>
                  )}
                </tbody>
              </DataTableElement>
            </div>
          )}
        </DataTableSurface>
      </div>

      <ModalActions className={cx(styles['prompt-similarity-actions'])}>
        <Button disabled={isSaving} onClick={onClose} variant="secondary">Cancel</Button>
        <Button disabled={!canSave || isSaving} onClick={() => void save()} variant="primary">
          {isSaving ? 'Saving...' : prompt.pool ? 'Update pool' : 'Create pool'}
        </Button>
      </ModalActions>

      <div className={styles['prompt-similarity-player']}>
        <AdminAudioPlayer
          className={styles['prompt-similarity-player-chrome']}
          currentTime={audioCurrentTime}
          duration={audioDuration}
          isPlaying={audioPlaying}
          loading={audioLoading}
          muted={audioMuted}
          track={playingTrack}
          volume={audioVolume}
          onSeek={onSeek}
          onTogglePlayback={onTogglePlayback}
          onToggleMute={onToggleMute}
          onVolumeChange={onVolumeChange}
          onStop={onStopPlayback}
          {...(onNextTrack ? { onNext: onNextTrack } : {})}
        />
      </div>
    </Modal>
  )
}

const promptTrackPageSize = 25

export const SunoPromptTracksDrawer = ({ onChanged, onClose, prompt }: SunoPromptTracksDrawerProps) => {
  const { toast } = useToast()
  const [data, setData] = useState<SunoPromptTrackListResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([])
  const {
    playingTrack,
    audioLoading,
    audioPlaying,
    audioCurrentTime,
    audioDuration,
    playTrack,
    seekPlayback
  } = useAudioPlayer()
  const totalPages = Math.max(
    1,
    Math.ceil(Math.max(data?.linkedTotal ?? 0, data?.matchingTotal ?? 0) / promptTrackPageSize)
  )

  const loadTracks = useCallback(async (nextPage: number, nextSearch: string): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const result = await getSunoPromptTracks(prompt.id, {
        page: nextPage,
        pageSize: promptTrackPageSize,
        search: nextSearch
      })
      setData(result)
      setSelectedTrackIds((current) =>
        current.filter((trackId) => result.matching.some((track) => track.id === trackId))
      )
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load linked tracks.')
    } finally {
      setIsLoading(false)
    }
  }, [prompt.id])

  useEffect(() => {
    void loadTracks(1, '')
  }, [loadTracks])

  const submitSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setPage(1)
    void loadTracks(1, search)
  }

  const toggleTrack = (trackId: string): void => {
    setSelectedTrackIds((current) =>
      current.includes(trackId)
        ? current.filter((id) => id !== trackId)
        : [...current, trackId]
    )
  }

  const selectAllVisible = (): void => {
    const visibleIds = data?.matching.map((track) => track.id) ?? []
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedTrackIds.includes(id))
    setSelectedTrackIds(allSelected ? [] : visibleIds)
  }

  const linkTracks = async (linkAllMatching = false): Promise<void> => {
    setIsSaving(true)
    try {
      const result = await linkSunoPromptTracks({
        linkAllMatching,
        promptId: prompt.id,
        search,
        trackIds: linkAllMatching ? undefined : selectedTrackIds
      })
      toast(`Linked ${result.linkedCount} track(s).`)
      setSelectedTrackIds([])
      await loadTracks(page, search)
      onChanged()
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to link tracks.')
    } finally {
      setIsSaving(false)
    }
  }

  const unlinkTrack = async (track: SunoPromptTrackBindingSummary): Promise<void> => {
    setIsSaving(true)
    try {
      await unlinkAudioTrackSunoPrompt(track.id)
      toast(`Unlinked "${track.title}".`)
      await loadTracks(page, search)
      onChanged()
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to unlink track.')
    } finally {
      setIsSaving(false)
    }
  }

  const changePage = (nextPage: number): void => {
    const boundedPage = Math.min(totalPages, Math.max(1, nextPage))
    setPage(boundedPage)
    void loadTracks(boundedPage, search)
  }

  return (
    <Drawer
      ariaLabelledBy="prompt-tracks-title"
      className={styles['prompt-tracks-drawer']}
      closeLabel="Close linked tracks"
      isDismissDisabled={isSaving}
      onClose={onClose}
      subtitle={prompt.title}
      title={<h2 id="prompt-tracks-title">Linked tracks</h2>}
    >
      <form className={styles['prompt-library-search']} onSubmit={submitSearch}>
        <div className={cx('search-field', styles['prompt-search-field'])}>
          <Search size={16} />
          <input
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tracks..."
            value={search}
          />
        </div>
        <Button disabled={isLoading || isSaving} type="submit">
          {isLoading ? <Loader2 className="tools-spin" size={16} /> : <Search size={16} />}
          Search
        </Button>
      </form>

      {error && <div className={styles['tools-error']}>{error}</div>}

      <PromptTrackSection
        audioCurrentTime={audioCurrentTime}
        audioDuration={audioDuration}
        audioPlaying={audioPlaying}
        emptyText="No linked tracks."
        isLinked
        items={data?.linked ?? []}
        loadingTrackId={audioLoading ? playingTrack?.id ?? null : null}
        playingTrackId={playingTrack?.id ?? null}
        title={<><strong>Linked</strong> <span className={styles['prompt-track-count']}>{data?.linkedTotal ?? prompt.linked_track_count ?? 0}</span></>}
        onPlayTrack={playTrack}
        onSeek={seekPlayback}
        onUnlink={(track) => void unlinkTrack(track as SunoPromptTrackBindingSummary)}
      />

      <strong>Exact unlinked matches <span className={styles['prompt-track-count']}>{data ? data.matchingTotal : 0}</span></strong>

      {data?.matching.length ? (
        <>
          <div className={styles['prompt-tracks-toolbar']}>
            <label className={styles['prompt-tracks-select-all']}>
              <input
                checked={data.matching.every((track) => selectedTrackIds.includes(track.id))}
                disabled={isSaving}
                onChange={selectAllVisible}
                type="checkbox"
              />
              <span>Select all visible</span>
            </label>
            <Button
              disabled={!data.matchingTotal || isSaving}
              onClick={() => void linkTracks(true)}
            >
              Link all{data.matchingTotal ? ` ${data.matchingTotal} matches` : ''}
            </Button>
          </div>

          <PromptTrackSection
            audioCurrentTime={audioCurrentTime}
            audioDuration={audioDuration}
            audioPlaying={audioPlaying}
            emptyText="No unlinked exact matches."
            items={data.matching}
            loadingTrackId={audioLoading ? playingTrack?.id ?? null : null}
            playingTrackId={playingTrack?.id ?? null}
            selectedTrackIds={selectedTrackIds}
            title=""
            onPlayTrack={playTrack}
            onSeek={seekPlayback}
            onToggle={toggleTrack}
          />

          <Button
            className={cx(styles['prompt-tracks-link-all'])}
            disabled={selectedTrackIds.length === 0 || isSaving}
            onClick={() => void linkTracks(false)}
            variant="primary"
          >
            {isSaving ? <Loader2 className="tools-spin" size={16} /> : <Link2 size={16} />}
            Link selected{selectedTrackIds.length > 0 ? ` (${selectedTrackIds.length})` : ''}
          </Button>

          {totalPages > 1 && (
            <TablePagination
              className={cx(styles['prompt-tracks-pagination'])}
              currentPage={page}
              isNextDisabled={page >= totalPages || isLoading}
              isPreviousDisabled={page <= 1 || isLoading}
              nextLabel="Next"
              onNext={() => changePage(page + 1)}
              onPrevious={() => changePage(page - 1)}
              previousLabel="Previous"
              totalPages={totalPages}
            />
          )}
        </>
      ) : (
        <PromptTrackSection
          emptyText="No unlinked exact matches."
          items={[]}
          title=""
        />
      )}
    </Drawer>
  )
}

type PromptTrackSectionProps = {
  audioCurrentTime?: number
  audioDuration?: number
  audioPlaying?: boolean
  emptyText: string
  isLinked?: boolean
  items: Array<SunoPromptTrackSummary | SunoPromptTrackBindingSummary>
  loadingTrackId?: string | null
  onPlayTrack?: (track: PlayableTrack) => void
  onSeek?: (time: number) => void
  onToggle?: (trackId: string) => void
  onUnlink?: (track: SunoPromptTrackSummary | SunoPromptTrackBindingSummary) => void
  playingTrackId?: string | null
  selectedTrackIds?: string[]
  title?: ReactNode
}

const PromptTrackSection = ({
  audioCurrentTime = 0,
  audioDuration = 0,
  audioPlaying = false,
  emptyText,
  isLinked = false,
  items,
  loadingTrackId,
  onPlayTrack,
  onSeek,
  onToggle,
  onUnlink,
  playingTrackId,
  selectedTrackIds = [],
  title
}: PromptTrackSectionProps) => {
  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (!onSeek || audioDuration <= 0) return
    const rect = event.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    onSeek(ratio * audioDuration)
  }

  return (
    <section className={styles['prompt-track-section']}>
      {title && <div className={styles['prompt-track-section-title']}>{title}</div>}
      {items.length === 0 ? (
        <span className="muted">{emptyText}</span>
      ) : (
        <ol className={styles['prompt-track-list']}>
          {items.map((track) => {
            const selected = selectedTrackIds.includes(track.id)
            const isCurrentTrack = playingTrackId === track.id
            const isLoadingTrack = loadingTrackId === track.id
            const progressRatio = audioDuration > 0 ? audioCurrentTime / audioDuration : 0

            return (
              <li className={cx(styles['prompt-track-item'], isCurrentTrack && 'playing', isLinked ? styles['prompt-track-item--linked'] : styles['prompt-track-item--unlinked'])} key={track.id}>
                {onPlayTrack && (
                  <IconButton
                    disabled={isLoadingTrack}
                    icon={isLoadingTrack ? (
                      <Loader2 className="tools-spin" size={15} />
                    ) : isCurrentTrack && audioPlaying ? (
                      <Pause size={15} />
                    ) : (
                      <Play size={15} />
                    )}
                    iconSize="play"
                    label={isCurrentTrack && audioPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
                    loading={isLoadingTrack}
                    onClick={() => onPlayTrack(track)}
                  />
                )}
                {!isLinked && (
                  <button
                    aria-label={`${selected ? 'Deselect' : 'Select'} ${track.title}`}
                    className={selected ? cx('track-suggestion-check', 'selected') : cx('track-suggestion-check')}
                    onClick={() => onToggle?.(track.id)}
                    type="button"
                  >
                    {selected && <Check size={13} />}
                  </button>
                )}
                <span className={styles['prompt-track-copy']}>
                  <small>{track.artist || 'Unknown artist'}</small>
                  <span aria-hidden="true">-</span>
                  <strong>{track.title}</strong>
                </span>
                {isLinked && (
                  <IconButton
                    className={styles['prompt-track-unlink-button']}
                    icon={<Unlink size={15} />}
                    label={`Unlink ${track.title}`}
                    onClick={() => onUnlink?.(track)}
                    title="Unlink"
                    variant="danger"
                  />
                )}
                {isCurrentTrack && onSeek && (
                  <div className={styles['prompt-track-progress']} onClick={handleProgressClick} role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progressRatio * 100)}>
                    <div className={styles['prompt-track-progress-fill']} style={{ width: `${progressRatio * 100}%` }} />
                  </div>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
